import { compact, omit, uniq } from 'lodash';

import { getValidRelationTypesForOperation } from '../../edit-pipeline/utils/relation';
import { getModelOrThrow } from '../../model/utils';
import { isRelationGrouping, PipelineStateContext } from '../../pipeline/state';
import type {
  AddRelatedColumnOperation,
  Field,
  FunnelOperation,
  FunnelStep,
  Grouping,
  Pipeline,
  PipelineOperation,
  PipelineState,
  PipelineStateRelation,
} from '../../types';
import {
  getFlattenedStepPipeline,
  getFunnelPipelineStates,
  getSharedFields,
  getStepPipelineState,
} from '../utils';

const getSharedRelations = (pipelineStates: PipelineState[]) => {
  if (pipelineStates.length === 0) {
    return [];
  }

  const relations = pipelineStates.map((state) => state.relations);
  const [firstRelations, ...restRelations] = relations;
  const sharedRelations = firstRelations
    .filter((relation) =>
      getValidRelationTypesForOperation('addRelatedColumn').includes(relation.type),
    )
    .reduce<PipelineStateRelation[]>((acc, relation) => {
      if (
        restRelations.every((r) => Boolean(r.find(({ modelId }) => modelId === relation.modelId)))
      ) {
        return [...acc, relation];
      }

      return acc;
    }, []);

  return sharedRelations;
};

const filterAddedRelatedColumns = (operation: AddRelatedColumnOperation, keys: string[]) => ({
  ...operation,
  parameters: {
    ...operation.parameters,
    columns: operation.parameters.columns.filter((column) => keys.includes(column.property.key)),
  },
});

const removeUnusedRelatedColumns = (funnelOperation: FunnelOperation): FunnelOperation => {
  const funnelGroupKeys = funnelOperation.parameters.groups?.map((group) => group.key) ?? [];
  return {
    ...funnelOperation,
    parameters: {
      ...funnelOperation.parameters,
      steps: funnelOperation.parameters.steps.map((step) => ({
        ...step,
        pipeline: {
          ...step.pipeline,
          operations: step.pipeline.operations
            .map((operation) =>
              operation.operation !== 'addRelatedColumn'
                ? operation
                : filterAddedRelatedColumns(operation, funnelGroupKeys),
            )
            .filter(
              (operation) =>
                operation.operation !== 'addRelatedColumn' ||
                operation.parameters.columns.length > 0,
            ),
        },
      })),
    },
  };
};

const addRelatedColumnOperationExists = (
  pipeline: Pipeline,
  operation: AddRelatedColumnOperation,
) =>
  operation.parameters.columns.every((column) =>
    pipeline.operations.some(
      (operation) =>
        operation.operation === 'addRelatedColumn' &&
        operation.parameters.columns.some(
          (existingColumn) => existingColumn.property.key === column.property.key,
        ),
    ),
  );

export const generateGroupColumnKey = (modelId: string | undefined, key: string) =>
  `${modelId !== undefined ? `${modelId}_` : ''}${key}`;

const findGroupRelation = (relations: PipelineStateRelation[], modelId: string) =>
  relations.find(
    (relation) =>
      getValidRelationTypesForOperation('addRelatedColumn').includes(relation.type) &&
      relation.modelId === modelId,
  );

const createAddRelatedColumnOperationsForGroups = (
  relations: PipelineStateRelation[],
  groups: Grouping[],
): AddRelatedColumnOperation[] => {
  return compact(
    groups.map((group) => {
      if (!isRelationGrouping(group)) {
        return null;
      }

      const relation = findGroupRelation(relations, group.relation.key);

      if (relation === undefined) {
        return null;
      }

      const columnKey = generateGroupColumnKey(group.relation.key, group.key);

      return {
        operation: 'addRelatedColumn',
        parameters: {
          relation: { key: relation.key, modelId: relation.baseModelId },
          columns: [
            {
              key: group.key,
              property: {
                key: columnKey,
                name: columnKey,
              },
            },
          ],
        },
      };
    }),
  );
};

/**
 * Return the intersection of pipeline fields and the fields of the intersection of related models of all steps
 * grouped by models.
 */
export const getGroupableFields = (steps: FunnelStep[], ctx: PipelineStateContext) => {
  const pipelineStates = getFunnelPipelineStates(steps, ctx);
  const countingFields = uniq(steps.flatMap((step) => step.fields));
  const sharedFields = getSharedFields(pipelineStates);
  const sharedRelations = getSharedRelations(pipelineStates);

  const canGroupByField = (field: Field) => !countingFields.some((key) => key === field.key);

  const sharedRelatedFields = sharedRelations.map((relation) => {
    const model = getModelOrThrow(ctx.models, relation.modelId);
    return {
      key: relation.modelId,
      name: `on ${model.name}`,
      fields: model.properties.filter(canGroupByField).map((property) => ({
        ...property,
        relation: { ...relation, key: relation.modelId },
        model: { ...model, propertyKey: property.key },
        name: property.name,
        key: property.key,
      })),
    };
  });

  return [
    ...sharedFields.filter(canGroupByField),
    ...sharedRelatedFields.filter((group) => group.fields.length > 0),
  ];
};

const operationAddsColumn = (
  operation: PipelineOperation,
  key: string,
): operation is AddRelatedColumnOperation =>
  operation.operation === 'addRelatedColumn' &&
  operation.parameters.columns.some((column) => column.property.key === key);

const stepAddsColumn = (step: FunnelStep, key: string) =>
  step.pipeline.operations.some((operation) => operationAddsColumn(operation, key));

/**
 * Resolve a grouping with a reference to an addRelatedColumn operation output to a grouping with a reference to the underlying field.
 */
const resolveFunnelGroup = (
  groupKey: string,
  funnelOperation: FunnelOperation,
  ctx: PipelineStateContext,
) => {
  const step = funnelOperation.parameters.steps.find((step) => stepAddsColumn(step, groupKey));
  const operation = step?.pipeline.operations.find((operation) =>
    operationAddsColumn(operation, groupKey),
  );
  if (!step || !operation) {
    return {
      modelId: undefined,
      key: groupKey,
    };
  }
  const { relations } = getStepPipelineState(step, ctx);
  const { modelId, key } = operation.parameters.relation;
  const relation = relations.find(
    (relation) =>
      relation.key === key && (modelId === undefined || relation.baseModelId === modelId),
  );
  return {
    modelId: relation?.modelId,
    key: operation.parameters.columns[0].key,
  };
};

export const resolveFunnelGroups = (operation: FunnelOperation, ctx: PipelineStateContext) => {
  return (
    operation.parameters.groups?.map((group) => {
      const { modelId, key } = resolveFunnelGroup(group.key, operation, ctx);
      return {
        ...group,
        key,
        relation: modelId !== undefined ? { key: modelId } : undefined,
        property: true,
      };
    }) ?? []
  );
};

/**
 * Generate new addRelatedColumnOperations and transform groups to point to their output
 * group.relation.key is used as modelId
 */
export const updateGroupings = (
  funnelOperation: FunnelOperation,
  groups: Grouping[],
  ctx: PipelineStateContext,
): FunnelOperation => {
  // Steps that don't resolve to a model (empty ones and ones referencing a missing block)
  // have no known relations, so they neither receive columns nor veto a grouping.
  const stepRelations = funnelOperation.parameters.steps.map((step) =>
    getFlattenedStepPipeline(step, ctx.exploration).baseModelId === ''
      ? null
      : getStepPipelineState(step, ctx).relations,
  );

  // A relation grouping only applies if every resolvable step can reach the related model.
  const applicableGroups = groups.filter(
    (group) =>
      !isRelationGrouping(group) ||
      stepRelations.every(
        (relations) =>
          relations === null || findGroupRelation(relations, group.relation.key) !== undefined,
      ),
  );

  const transformedGroups = applicableGroups.map((group) => ({
    ...group,
    key: generateGroupColumnKey(
      isRelationGrouping(group) ? group.relation.key : undefined,
      group.key,
    ),
  }));

  const updatedOperation = {
    ...funnelOperation,
    parameters: {
      ...omit(funnelOperation.parameters, 'groups'),
      ...(transformedGroups.length > 0
        ? {
            groups: transformedGroups,
          }
        : {}),
      steps: funnelOperation.parameters.steps.map((step, index) => {
        const relations = stepRelations[index];
        if (relations === null) {
          return step;
        }
        const addRelatedColumnOperations = createAddRelatedColumnOperationsForGroups(
          relations,
          applicableGroups,
        );
        return {
          ...step,
          pipeline: {
            ...step.pipeline,
            operations: [
              ...step.pipeline.operations,
              ...addRelatedColumnOperations.filter(
                (operation) => !addRelatedColumnOperationExists(step.pipeline, operation),
              ),
            ],
          },
        };
      }),
    },
  };
  return removeUnusedRelatedColumns(updatedOperation);
};
